import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { LogOut, CheckCircle2, Ban, Users, Building2, Gift, Trash2, Mail, Send, Check, Edit2, Plus } from "lucide-react";

export default function AdminWorkspaces() {
  const { user, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // Super-admin notification email -- where approval emails are delivered.
  const [notifEmail, setNotifEmail] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  // Manual-create modal (for reconciling Stripe-first signups, etc.)
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    company_name: "", admin_username: "", admin_password: "",
    status: "active", paywall_exempt: false,
  });
  const [creating, setCreating] = useState(false);

  const openCreate = () => {
    setCreateForm({ company_name: "", admin_username: "", admin_password: "", status: "active", paywall_exempt: false });
    setCreateOpen(true);
  };

  const createWorkspace = async () => {
    const name = createForm.company_name.trim();
    const uname = createForm.admin_username.trim().toLowerCase();
    if (!name) { toast.error("Company name is required"); return; }
    if (!uname) { toast.error("Admin username is required"); return; }
    if ((createForm.admin_password || "").length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setCreating(true);
    try {
      await api.post("/admin/workspaces", {
        company_name: name,
        admin_username: uname,
        admin_password: createForm.admin_password,
        status: createForm.status,
        paywall_exempt: !!createForm.paywall_exempt,
      });
      toast.success(`"${name}" created · admin "${uname}" can sign in now`);
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not create workspace"));
    } finally { setCreating(false); }
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/workspaces")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(errMsg(e, "Could not load workspaces")))
      .finally(() => setLoading(false));
  };

  const loadNotif = () => {
    api.get("/admin/notification-email")
      .then((r) => { setNotifEmail(r.data.my_email || ""); setEmailDraft(r.data.my_email || ""); })
      .catch(() => {});
  };

  useEffect(() => { load(); loadNotif(); }, []);

  const saveEmail = async () => {
    const v = (emailDraft || "").trim();
    if (v && !v.includes("@")) { toast.error("Enter a valid email"); return; }
    setSavingEmail(true);
    try {
      await api.post("/admin/notification-email", { email: v });
      setNotifEmail(v);
      setEditingEmail(false);
      toast.success(v ? "Notification email saved" : "Email cleared — notifications disabled");
    } catch (e) {
      toast.error(errMsg(e, "Could not save email"));
    } finally { setSavingEmail(false); }
  };

  const resendEmail = async (company_id, name) => {
    setBusy(company_id + "resend");
    try {
      const r = await api.post(`/admin/workspaces/${company_id}/resend-approval-email`);
      toast.success(`Approval link emailed to ${r.data.sent_to} super-admin inbox${r.data.sent_to === 1 ? "" : "es"}`);
    } catch (e) {
      toast.error(errMsg(e, "Could not resend email"));
    } finally { setBusy(null); }
  };

  const act = async (company_id, action, name) => {
    if (action === "suspend" && !window.confirm(`Suspend ${name}? Their admins and drivers can no longer sign in.`)) return;
    setBusy(company_id + action);
    try {
      await api.post(`/admin/workspaces/${company_id}/action`, { action });
      toast.success(`Workspace ${action}d`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Action failed"));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (company_id, name) => {
    const typed = window.prompt(
      `PERMANENTLY delete "${name}" and ALL of its drivers, trips, statements and users?\n\nThis cannot be undone.\n\nType the company name to confirm:`
    );
    if (typed === null) return;
    if (typed.trim() !== name) {
      toast.error("Name did not match — nothing deleted");
      return;
    }
    setBusy(company_id + "delete");
    try {
      const r = await api.delete(`/admin/workspaces/${company_id}`);
      const n = Object.values(r.data?.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Deleted "${name}" (${n} record${n === 1 ? "" : "s"} removed)`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not delete workspace"));
    } finally {
      setBusy(null);
    }
  };

  const toggleExempt = async (company_id, currentExempt, name) => {
    const next = !currentExempt;
    const label = next
      ? `Make "${name}" free (no trial clock, no Stripe required)?`
      : `Remove free status from "${name}"? They'll go back on the normal trial / subscription track.`;
    if (!window.confirm(label)) return;
    setBusy(company_id + "exempt");
    try {
      await api.post(`/admin/workspaces/${company_id}/exempt`, { exempt: next });
      toast.success(next ? `${name} is now free (no billing)` : `${name} back on paid track`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not change exemption"));
    } finally {
      setBusy(null);
    }
  };

  const pill = (status, exempt) => {
    if (exempt) {
      return <span className="text-[11px] font-semibold px-2 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200">Free · Exempt</span>;
    }
    const map = {
      pending: ["bg-amber-50 text-amber-700 border-amber-200", "Pending"],
      active: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Active"],
      suspended: ["bg-rose-50 text-rose-700 border-rose-200", "Suspended"],
    };
    const [cls, label] = map[status] || ["bg-slate-100 text-slate-700 border-slate-200", status];
    return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${cls}`}>{label}</span>;
  };

  // Sort pending first (sticky visibility for new signups), then by created_at desc.
  const sortedRows = [...rows].sort((a, b) => {
    const ap = a.status === "pending" ? 0 : 1;
    const bp = b.status === "pending" ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return (b.created_at || "").localeCompare(a.created_at || "");
  });
  const pendingCount = rows.filter((r) => r.status === "pending").length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-slate-900 grid place-items-center text-white"><Building2 size={18} /></div>
            <div>
              <div className="text-sm font-semibold text-slate-900">Platform Admin</div>
              <div className="text-[11px] text-slate-500">Workspaces · {user?.username}</div>
            </div>
          </div>
          <Button variant="outline" onClick={logout} data-testid="admin-logout-btn">
            <LogOut size={14} className="mr-1" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6">
        <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              Workspaces
              {pendingCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-2 rounded-full bg-amber-500 text-white text-[11px] font-bold" data-testid="pending-badge">
                  {pendingCount}
                </span>
              )}
            </h1>
            <p className="text-sm text-slate-500">Review signups, approve access, or suspend misuse.</p>
          </div>
          <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={openCreate} data-testid="create-workspace-btn">
            <Plus size={16} className="mr-1.5" /> Create workspace
          </Button>
        </div>

        {/* Notification-email widget */}
        <div className={`mb-5 rounded-lg border p-4 flex items-start gap-3 flex-wrap ${notifEmail ? "bg-white border-slate-200" : "bg-amber-50 border-amber-200"}`} data-testid="notif-email-widget">
          <Mail className={notifEmail ? "text-slate-500 shrink-0 mt-0.5" : "text-amber-700 shrink-0 mt-0.5"} size={18} />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-slate-700">Signup approval emails are sent to</div>
            {!editingEmail ? (
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                {notifEmail ? (
                  <span className="font-mono text-sm text-slate-900" data-testid="notif-email-value">{notifEmail}</span>
                ) : (
                  <span className="text-sm text-amber-800">No email set — nobody is being notified of new signups.</span>
                )}
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => { setEditingEmail(true); setEmailDraft(notifEmail); }} data-testid="notif-email-edit-btn">
                  <Edit2 size={11} className="mr-1" /> {notifEmail ? "Change" : "Set email"}
                </Button>
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <Input
                  type="email" value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)}
                  placeholder="you@yourdomain.com" className="h-8 text-sm max-w-xs"
                  data-testid="notif-email-input"
                />
                <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={saveEmail} disabled={savingEmail} data-testid="notif-email-save-btn">
                  <Check size={12} className="mr-1" /> {savingEmail ? "Saving…" : "Save"}
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => { setEditingEmail(false); setEmailDraft(notifEmail); }}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-slate-500">Loading workspaces...</div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">
            No workspaces yet.
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto" data-testid="workspaces-table">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Trial ends</th>
                  <th className="px-4 py-3">Admins</th>
                  <th className="px-4 py-3">Drivers</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((w) => (
                  <tr key={w.company_id} className={w.status === "pending" ? "border-t border-slate-100 bg-amber-50/40" : "border-t border-slate-100"} data-testid={`ws-${w.company_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{w.name}</div>
                      <div className="text-[11px] font-mono text-slate-500">{w.company_id.slice(0, 8)}…</div>
                    </td>
                    <td className="px-4 py-3">{pill(w.status, w.paywall_exempt)}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {w.paywall_exempt ? "∞ free"
                        : (w.trial_ends_at ? new Date(w.trial_ends_at).toLocaleDateString() : "—")}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{w.admin_count}</td>
                    <td className="px-4 py-3 text-slate-700 flex items-center gap-1"><Users size={13} /> {w.driver_count}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2 flex-wrap">
                        {w.status === "pending" && (
                          <>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => act(w.company_id, "approve", w.name)}
                              disabled={busy === w.company_id + "approve"}
                              data-testid={`approve-${w.company_id}`}
                            >
                              <CheckCircle2 size={14} className="mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-slate-700 border-slate-300 hover:bg-slate-100"
                              onClick={() => resendEmail(w.company_id, w.name)}
                              disabled={busy === w.company_id + "resend"}
                              data-testid={`resend-${w.company_id}`}
                              title="Resend the approval email to all super-admins"
                            >
                              <Send size={13} className="mr-1" /> Resend email
                            </Button>
                          </>
                        )}
                        {w.status === "suspended" && (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => act(w.company_id, "activate", w.name)}
                            disabled={busy === w.company_id + "activate"}
                            data-testid={`activate-${w.company_id}`}
                          >
                            Re-activate
                          </Button>
                        )}
                        {w.status === "active" && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className={w.paywall_exempt
                                ? "text-indigo-700 border-indigo-300 bg-indigo-50 hover:bg-indigo-100"
                                : "text-indigo-700 border-indigo-200 hover:bg-indigo-50"}
                              onClick={() => toggleExempt(w.company_id, w.paywall_exempt, w.name)}
                              disabled={busy === w.company_id + "exempt"}
                              data-testid={`exempt-${w.company_id}`}
                              title={w.paywall_exempt ? "Click to put back on paid plan" : "Click to make free (no billing)"}
                            >
                              <Gift size={14} className="mr-1" /> {w.paywall_exempt ? "Free" : "Comp"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-rose-700 border-rose-200 hover:bg-rose-50"
                              onClick={() => act(w.company_id, "suspend", w.name)}
                              disabled={busy === w.company_id + "suspend"}
                              data-testid={`suspend-${w.company_id}`}
                            >
                              <Ban size={14} className="mr-1" /> Suspend
                            </Button>
                          </>
                        )}
                        {w.slug !== "legacy" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-rose-800 border-rose-300 hover:bg-rose-100"
                            onClick={() => remove(w.company_id, w.name)}
                            disabled={busy === w.company_id + "delete"}
                            data-testid={`delete-${w.company_id}`}
                            title="Permanently delete this workspace and all its data"
                          >
                            <Trash2 size={14} className="mr-1" /> Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create a workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Use this for customers who paid in Stripe before completing signup, or to spin up an account on someone's behalf.
              The admin can sign in immediately with the username + password you set here.
            </p>
            <div>
              <Label className="text-xs">Company name</Label>
              <Input value={createForm.company_name}
                     onChange={(e) => setCreateForm((p) => ({ ...p, company_name: e.target.value }))}
                     placeholder="Transtarr Express LLC" className="h-10"
                     data-testid="cw-name" />
            </div>
            <div>
              <Label className="text-xs">Admin username</Label>
              <Input value={createForm.admin_username}
                     onChange={(e) => setCreateForm((p) => ({ ...p, admin_username: e.target.value }))}
                     placeholder="transtarr_admin" className="h-10 font-mono text-sm"
                     data-testid="cw-username" />
            </div>
            <div>
              <Label className="text-xs">Admin password (min 6 chars)</Label>
              <Input type="text" value={createForm.admin_password}
                     onChange={(e) => setCreateForm((p) => ({ ...p, admin_password: e.target.value }))}
                     placeholder="Share this with the customer"
                     className="h-10 font-mono text-sm" data-testid="cw-password" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select value={createForm.status}
                      onChange={(e) => setCreateForm((p) => ({ ...p, status: e.target.value }))}
                      className="w-full h-10 rounded-md border border-slate-300 bg-white px-2 text-sm"
                      data-testid="cw-status">
                <option value="active">Active (can sign in immediately)</option>
                <option value="pending">Pending (needs approval before sign-in)</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={createForm.paywall_exempt}
                        onCheckedChange={(v) => setCreateForm((p) => ({ ...p, paywall_exempt: !!v }))}
                        data-testid="cw-exempt" />
              Free · exempt (no billing, unlimited drivers)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={createWorkspace} disabled={creating} data-testid="cw-submit">
              {creating ? "Creating…" : "Create workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
